import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Stack } from "@mui/material";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";
import RecipeReadOnlyView from "../components/RecipeReadOnlyView";
import {
  getRecipe,
  getRecipeVersion,
  getRecipeVersions,
} from "../api/recipesApi";

function listFromPaginated(data) {
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data)) return data;
  return [];
}

function errorToMessage(error) {
  const message =
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.data?.message ||
    error?.message ||
    "Error al cargar la receta.";
  return Array.isArray(message) ? message.join(" | ") : message;
}

export default function RecipeDetailPage() {
  const navigate = useNavigate();
  const { recipeId } = useParams();
  const numericRecipeId = Number(recipeId);

  const [recipe, setRecipe] = useState(null);
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadVersion, setReloadVersion] = useState(0);

  const fetchRecipeDetail = useCallback(
    async (signal) => {
      setLoading(true);
      setError("");

      try {
        const [recipeData, versionsData] = await Promise.all([
          getRecipe(numericRecipeId, { signal }),
          getRecipeVersions(numericRecipeId, { page: 1, limit: 1, signal }),
        ]);
        if (signal.aborted) return;

        const firstVersion = listFromPaginated(versionsData)[0] || null;
        setRecipe(recipeData);

        if (!firstVersion?.id) {
          setVersion(null);
          return;
        }

        const versionData = await getRecipeVersion(
          numericRecipeId,
          firstVersion.id,
          { signal }
        );
        if (!signal.aborted) setVersion(versionData);
      } catch (requestError) {
        if (signal.aborted) return;
        setRecipe(null);
        setVersion(null);
        setError(errorToMessage(requestError));
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [numericRecipeId]
  );

  useEffect(() => {
    if (!Number.isFinite(numericRecipeId) || numericRecipeId <= 0) return undefined;
    const controller = new AbortController();
    fetchRecipeDetail(controller.signal);
    return () => controller.abort();
  }, [fetchRecipeDetail, numericRecipeId, reloadVersion]);

  if (!Number.isFinite(numericRecipeId) || numericRecipeId <= 0) {
    return <Navigate to="/stock/base-de-datos/recetas" replace />;
  }

  if (error) {
    return (
      <Stack spacing={2} sx={{ p: 2, flex: 1, height: "100%", minHeight: 0, overflow: "hidden" }}>
        <StockPageHeader
          title="Detalle de receta"
          onBack={() => navigate("/stock/base-de-datos/recetas")}
        />
        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", bgcolor: "neutral.50", borderRadius: "22px", p: 3 }}>
          <Stack spacing={1.5} sx={{ height: "100%", minHeight: 220, justifyContent: "center" }}>
            <Alert severity="error">{error}</Alert>
            <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
              <Button onClick={() => navigate("/stock/base-de-datos/recetas")}>
                Volver
              </Button>
              <Button variant="contained" onClick={() => setReloadVersion((value) => value + 1)}>
                Reintentar
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        flex: 1,
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        "@media (max-height: 700px)": { p: 1.25, gap: 1 },
      }}
    >
      <StockPageHeader
        title={recipe?.name || "Detalle de receta"}
        onBack={() => navigate("/stock/base-de-datos/recetas")}
      />
      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <RecipeReadOnlyView recipe={recipe} version={version} loading={loading} />
      </Box>
    </Stack>
  );
}
